/**
 * Currency utilities
 * Pure functions for currency operations
 */
export class CurrencyUtils {
    /**
     * Parses amount string to number
     * Removes all non-numeric characters except dot
     * @param amount - String representation of amount (e.g., "$123.45")
     * @returns Numeric value or 0 if parsing fails
     */
    static parseAmountToNumber(amount: string): number {
        const cleaned = amount.replace(/[^0-9.]/g, "");
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
    }

    /**
     * Formats number as USD currency with comma separators
     * Uses manual formatting to avoid SSR/client hydration mismatch from toLocaleString
     * @param value - Numeric value
     * @param hideCents - If true, rounds to whole number (default: true)
     * @returns Formatted string (e.g., "$1,629" or "$1,629.50")
     */
    static formatUSD(value: number, hideCents = true): string {
        const num = hideCents ? Math.round(value) : value;
        const [integerPart, decimalPart] = num.toFixed(hideCents ? 0 : 2).split(".");

        // Add comma separators manually
        const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        return hideCents ? `$${formatted}` : `$${formatted}.${decimalPart}`;
    }

    /**
     * Calculates sum of amounts from string array
     * @param amounts - Array of amount strings
     * @returns Sum of all amounts
     */
    static sumAmounts(amounts: Array<string>): number {
        return amounts.reduce((sum, amount) => sum + this.parseAmountToNumber(amount), 0);
    }
}
